import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import Editor, { useMonaco } from "@monaco-editor/react";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import DOMPurify from "dompurify";
import {
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  EmailTemplateAsset,
  EmailTemplateVariable,
  previewEmailTemplateBody,
  validateEmailTemplate,
} from "../../api/siteSettings";
import {
  convertDocxToHtml,
  getSystemVariables,
  SystemVariable,
} from "../../api/customEmail";
import convertToBase64 from "../../utils/convertToBase64";
import decodeBase64Utf8 from "../../utils/decodeBase64Utf8";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { usePermissionStore } from "../../store";
import {
  useDeleteEmailTemplateAsset,
  useGetEmailTemplateAssets,
  useGetEmailTemplateVariables,
  useUploadEmailTemplateAsset,
} from "../../queries/siteSettings";
import { useIsInternalUser } from "../../utils/useIsInternalUser";
import { isAmpEmailTemplate } from "./emailTemplateEditorHelpers";

const TINYMCE_API_KEY = "l8zomkw48zb8qhvwpahb2g5nodmnma6qjyz1acf2y14t53z6";
const PREVIEW_DEBOUNCE_MS = 400;

interface CloseEvent {
  editedData?: string;
  subject?: string;
}

interface DialogProps {
  templateName: string;
  templateContent: string;
  templateType: string;
  description?: string;
  recipient?: string;
  onClose: (data: CloseEvent) => void;
  subject?: string;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

interface SplitTemplateContent {
  templateHeader: string;
  templateBody: string;
  templateFooter: string;
}

const BODY_START = "<!-- body start -->";
const BODY_END = "<!-- body end -->";

function splitTemplate(templateContent: string): SplitTemplateContent {
  const headerIdx = templateContent.indexOf(BODY_START);
  const footerIdx = templateContent.lastIndexOf(BODY_END);
  if (headerIdx < 0 || footerIdx < 0 || footerIdx < headerIdx) {
    return {
      templateHeader: "",
      templateBody: templateContent,
      templateFooter: "",
    };
  }
  const header = templateContent.substring(0, headerIdx);
  const body = templateContent.substring(
    headerIdx + BODY_START.length,
    footerIdx
  );
  const footer = templateContent.substring(footerIdx + BODY_END.length);
  return { templateHeader: header, templateBody: body, templateFooter: footer };
}

function stripMarkers(value: string): string {
  return value.split(BODY_START).join("").split(BODY_END).join("");
}

function joinTemplate(header: string, body: string, footer: string): string {
  return (
    stripMarkers(header) +
    BODY_START +
    stripMarkers(body) +
    BODY_END +
    stripMarkers(footer)
  );
}

const BODY_START_TOKEN = "###HISSA_BODY_START###";
const BODY_END_TOKEN = "###HISSA_BODY_END###";

function sanitizeEmailHtml(dirty: string, wholeDocument: boolean): string {
  const withTokens = dirty
    .split(BODY_START)
    .join(BODY_START_TOKEN)
    .split(BODY_END)
    .join(BODY_END_TOKEN);
  const sanitized = DOMPurify.sanitize(withTokens, {
    WHOLE_DOCUMENT: wholeDocument,
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "form",
      "input",
      "button",
    ],
    ADD_TAGS: ["style"],
  });
  return sanitized
    .split(BODY_START_TOKEN)
    .join(BODY_START)
    .split(BODY_END_TOKEN)
    .join(BODY_END);
}

interface OutlookIssue {
  id: string;
  severity: "error" | "warning";
  message: string;
  index: number;
  length: number;
}

const OUTLOOK_RULES: Array<{
  id: string;
  severity: "error" | "warning";
  pattern: RegExp;
  message: string;
}> = [
  {
    id: "border-radius",
    severity: "error",
    pattern: /border-radius\s*:/i,
    message:
      "border-radius is used, but Outlook desktop ignores it, so corners will render square.",
  },
  {
    id: "box-shadow",
    severity: "error",
    pattern: /box-shadow\s*:/i,
    message: "box-shadow is used, which isn't supported in Outlook desktop.",
  },
  {
    id: "flex-grid",
    severity: "error",
    pattern: /display\s*:\s*(flex|grid|inline-flex|inline-grid)/i,
    message:
      "display: flex/grid is used, but Outlook desktop doesn't support either, so affected elements will render as plain blocks instead.",
  },
  {
    id: "float",
    severity: "error",
    pattern: /float\s*:\s*(left|right)/i,
    message:
      "float is used, which isn't supported in Outlook desktop; use a table-based layout instead.",
  },
  {
    id: "position",
    severity: "error",
    pattern: /position\s*:\s*(absolute|relative|fixed|sticky)/i,
    message:
      "CSS positioning is used, which isn't supported in Outlook desktop.",
  },
  {
    id: "background-image",
    severity: "error",
    pattern: /background(-image)?\s*:\s*[^;]*url\(/i,
    message:
      "A CSS background image is used, and Outlook desktop needs a VML fallback or it won't show.",
  },
  {
    id: "web-fonts",
    severity: "warning",
    pattern: /@font-face|fonts\.googleapis\.com/i,
    message:
      "A custom web font is referenced, but Outlook desktop ignores it and falls back to a default font.",
  },
  {
    id: "unsupported-elements",
    severity: "error",
    pattern: /<(video|audio|svg|canvas)[\s>]/i,
    message:
      "<video>/<audio>/<svg>/<canvas> is used, and none of these render in Outlook desktop.",
  },
  {
    id: "media-queries",
    severity: "warning",
    pattern: /@media/i,
    message:
      "@media queries are used, but Outlook desktop ignores them, so responsive/mobile-only styles won't apply.",
  },
  {
    id: "negative-margin",
    severity: "warning",
    pattern: /margin(-\w+)?\s*:\s*-\d/i,
    message:
      "A negative margin is used; Outlook desktop's handling of negative margins is unreliable.",
  },
];

function checkOutlookCompatibility(html: string): OutlookIssue[] {
  if (!html) return [];
  const issues: OutlookIssue[] = [];

  OUTLOOK_RULES.forEach((rule) => {
    const match = rule.pattern.exec(html);
    if (match) {
      issues.push({
        id: rule.id,
        severity: rule.severity,
        message: rule.message,
        index: match.index,
        length: match[0].length,
      });
    }
  });

  const imgRegex = /<img\b[^>]*>/gi;
  let imgMatch = imgRegex.exec(html);
  while (imgMatch !== null) {
    const tag = imgMatch[0];
    if (!/\bwidth\s*=/i.test(tag) || !/\bheight\s*=/i.test(tag)) {
      issues.push({
        id: "img-dimensions",
        severity: "warning",
        message:
          "Some <img> tags are missing explicit width/height, which Outlook can lay out inconsistently.",
        index: imgMatch.index,
        length: tag.length,
      });
      break;
    }
    imgMatch = imgRegex.exec(html);
  }

  return issues;
}

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

interface TagBalanceIssue {
  message: string;
  index: number;
  length: number;
}

function checkTagBalance(html: string): TagBalanceIssue[] {
  if (!html) return [];
  const stack: Array<{ tag: string; index: number; length: number }> = [];
  const issues: TagBalanceIssue[] = [];
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/)?>/g;
  let match: RegExpExecArray | null = tagRegex.exec(html);

  while (match !== null) {
    const [full, rawTag, selfClosingSlash] = match;
    const tag = rawTag.toLowerCase();
    const isClosing = full.startsWith("</");
    const isSelfClosing = Boolean(selfClosingSlash) || VOID_ELEMENTS.has(tag);

    if (!isSelfClosing) {
      if (isClosing) {
        if (stack[stack.length - 1]?.tag === tag) {
          stack.pop();
        } else {
          const matchIdx = stack.map((entry) => entry.tag).lastIndexOf(tag);
          if (matchIdx === -1) {
            issues.push({
              message: `Found a closing </${tag}> tag with no matching opening tag.`,
              index: match.index,
              length: full.length,
            });
          } else {
            stack.splice(matchIdx).forEach((entry, i) => {
              if (i > 0) {
                issues.push({
                  message: `<${entry.tag}> is opened but never closed before </${tag}> appears.`,
                  index: entry.index,
                  length: entry.length,
                });
              }
            });
          }
        }
      } else {
        stack.push({ tag, index: match.index, length: full.length });
      }
    }
    match = tagRegex.exec(html);
  }

  stack.forEach((entry) =>
    issues.push({
      message: `<${entry.tag}> is opened but never closed.`,
      index: entry.index,
      length: entry.length,
    })
  );
  return issues;
}

function EmailTemplateEditorEnhanced(props: DialogProps) {
  const {
    templateName,
    templateContent,
    templateType,
    description,
    recipient,
    onClose,
    subject,
    isFullscreen,
    onToggleFullscreen,
  } = props;
  const isAmpTemplate = isAmpEmailTemplate(templateType);

  const { permission } = usePermissionStore();
  const isUserEsopViewer = isEsopViewer();
  const monaco = useMonaco();
  const isInternalUser = useIsInternalUser();

  const [editMode, setEditMode] = useState<"Classic" | "HTML" | "Inline">(
    isAmpTemplate ? "HTML" : "Classic"
  );
  const [emailSubject, setEmailSubject] = useState(subject);
  const [splitContent, setSplitContent] = useState<SplitTemplateContent>({
    templateHeader: "",
    templateBody: "",
    templateFooter: "",
  });
  const [currentBody, setCurrentBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [importingWord, setImportingWord] = useState(false);
  const [pendingWordFile, setPendingWordFile] = useState<File | null>(null);
  const [pendingImportHasSelection, setPendingImportHasSelection] =
    useState(false);

  const [showAssets, setShowAssets] = useState(false);
  const [showOutlookCheck, setShowOutlookCheck] = useState(false);
  const [showTagBalanceCheck, setShowTagBalanceCheck] = useState(false);
  const PANE_HEIGHT = isFullscreen ? 780 : 560;

  const { data: assets = [], isLoading: assetsLoading } =
    useGetEmailTemplateAssets();
  const { mutate: uploadAsset, isLoading: assetUploading } =
    useUploadEmailTemplateAsset();
  const { mutate: deleteAsset } = useDeleteEmailTemplateAsset();
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const assetsRef = useRef<EmailTemplateAsset[]>([]);

  const { data: templateVariables = [], isLoading: variablesLoading } =
    useGetEmailTemplateVariables(templateType);
  const templateVariablesRef = useRef<EmailTemplateVariable[]>([]);
  const { data: systemVariables = [], isLoading: systemVariablesLoading } =
    useQuery({ queryKey: "systemVariables", queryFn: getSystemVariables });
  const systemVariablesRef = useRef<SystemVariable[]>([]);
  const [showVariables, setShowVariables] = useState(false);
  const [variableSearch, setVariableSearch] = useState("");

  const [previewHtml, setPreviewHtml] = useState("");
  const [ampPreviewHtml, setAmpPreviewHtml] = useState("");
  const [ampPreviewError, setAmpPreviewError] = useState("");
  const [ampPreviewLoading, setAmpPreviewLoading] = useState(false);
  const outlookIssues = useMemo(
    () => (isAmpTemplate ? [] : checkOutlookCompatibility(previewHtml)),
    [isAmpTemplate, previewHtml]
  );
  const tagBalanceIssues = useMemo(
    () => checkTagBalance(previewHtml),
    [previewHtml]
  );
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const previewScrollRef = useRef({ x: 0, y: 0 });

  const inlineIframeRef = useRef<HTMLIFrameElement>(null);
  const inlineSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tinyEditorRef = useRef<TinyEditor["editor"]>();
  const assetInputRef = useRef<HTMLInputElement>(null);
  const wordFileInputRef = useRef<HTMLInputElement>(null);

  const monacoEditorOptions = {
    autoClosingBrackets: "always" as const,
    inlineSuggest: { enabled: true, mode: "prefix" as const },
    language: "html" as const,
    suggest: { insertMode: "insert" as const },
    suggestSelection: "first" as const,
  };

  useEffect(() => {
    if (!templateContent) return;
    const split = splitTemplate(templateContent);
    setSplitContent(split);
    setCurrentBody(split.templateBody);
  }, [templateContent]);

  useEffect(() => {
    if (isAmpTemplate) setEditMode("HTML");
  }, [isAmpTemplate]);

  const applyTinyMceHeight = (editor: any) => {
    if (!editor) return;
    if (editor.theme?.resizeTo) {
      editor.theme.resizeTo("100%", PANE_HEIGHT);
    } else {
      const container = editor.getContainer?.();
      if (container) container.style.height = `${PANE_HEIGHT}px`;
    }
  };

  useEffect(() => {
    applyTinyMceHeight(tinyEditorRef.current);
  }, [isFullscreen, editMode, PANE_HEIGHT]);

  const buildPreview = useCallback(
    (body: string) => {
      if (isAmpTemplate) return body;
      const { templateHeader, templateFooter } = splitContent;
      return joinTemplate(templateHeader, body, templateFooter);
    },
    [isAmpTemplate, splitContent]
  );

  const schedulePreviewUpdate = useCallback(
    (body: string) => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => {
        setPreviewHtml(buildPreview(body));
      }, PREVIEW_DEBOUNCE_MS);
    },
    [buildPreview]
  );

  useEffect(() => {
    schedulePreviewUpdate(currentBody);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [currentBody, schedulePreviewUpdate]);

  useEffect(() => {
    if (!isAmpTemplate || !previewHtml) return undefined;
    let cancelled = false;
    setAmpPreviewLoading(true);
    setAmpPreviewError("");
    const base64Body = btoa(unescape(encodeURIComponent(previewHtml)));
    previewEmailTemplateBody(base64Body, templateType)
      .then((result) => {
        if (!cancelled) setAmpPreviewHtml(result.ampHtml || result.html);
      })
      .catch((err) => {
        if (!cancelled) {
          setAmpPreviewError(
            err.response?.data?.errorRaw || "AMP preview validation failed"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAmpPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAmpTemplate, previewHtml, templateType]);

  const handlePreviewLoad = () => {
    const win = previewIframeRef.current?.contentWindow;
    if (!win) return;
    win.scrollTo(previewScrollRef.current.x, previewScrollRef.current.y);
    win.addEventListener("scroll", () => {
      previewScrollRef.current = { x: win.scrollX, y: win.scrollY };
    });
  };

  const inlineSrcDoc = useMemo(() => {
    if (editMode !== "Inline") return "";
    return joinTemplate(
      splitContent.templateHeader,
      currentBody,
      splitContent.templateFooter
    );
  }, [editMode]);

  const handleInlineLoad = () => {
    const doc = inlineIframeRef.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.contentEditable = "true";
    doc.body.addEventListener("input", () => {
      if (inlineSyncTimerRef.current) clearTimeout(inlineSyncTimerRef.current);
      inlineSyncTimerRef.current = setTimeout(() => {
        const split = splitTemplate(doc.documentElement.outerHTML);
        setSplitContent(split);
        setCurrentBody(split.templateBody);
      }, PREVIEW_DEBOUNCE_MS);
    });
  };

  const isFullDocMode =
    (isInternalUser || isAmpTemplate) && editMode === "HTML";

  const renderedPreviewHtml = useMemo(
    () => (isAmpTemplate ? ampPreviewHtml : previewHtml),
    [ampPreviewHtml, isAmpTemplate, previewHtml]
  );

  const monacoCheckContent = isFullDocMode
    ? previewHtml
    : splitTemplate(previewHtml).templateBody;

  useEffect(() => {
    if (!monaco) return undefined;
    const model = monaco.editor.getEditors()[0]?.getModel();
    if (!model || editMode !== "HTML") return undefined;

    const toMarker = (
      issue: { message: string; index: number; length: number },
      severity: number
    ) => {
      const start = model.getPositionAt(issue.index);
      const end = model.getPositionAt(issue.index + issue.length);
      return {
        severity,
        message: issue.message,
        startLineNumber: start.lineNumber,
        startColumn: start.column,
        endLineNumber: end.lineNumber,
        endColumn: end.column,
      };
    };

    const markers = [
      ...checkTagBalance(monacoCheckContent).map((issue) =>
        toMarker(issue, monaco.MarkerSeverity.Error)
      ),
      ...checkOutlookCompatibility(monacoCheckContent).map((issue) =>
        toMarker(
          issue,
          issue.severity === "error"
            ? monaco.MarkerSeverity.Error
            : monaco.MarkerSeverity.Warning
        )
      ),
    ];
    monaco.editor.setModelMarkers(model, "email-template-checks", markers);

    return () => {
      monaco.editor.setModelMarkers(model, "email-template-checks", []);
    };
  }, [monaco, editMode, monacoCheckContent]);

  const getCurrentBody = (): string => {
    if (editMode === "Classic") {
      return tinyEditorRef.current?.getContent() ?? currentBody;
    }
    if (editMode === "Inline") {
      const doc = inlineIframeRef.current?.contentDocument;
      if (!doc?.documentElement) return currentBody;
      return splitTemplate(doc.documentElement.outerHTML).templateBody;
    }
    const monacoValue =
      monaco?.editor?.getEditors()[0]?.getValue() ?? currentBody;
    return isFullDocMode
      ? splitTemplate(monacoValue).templateBody
      : monacoValue;
  };

  const handleModeChange = (mode: "Classic" | "HTML" | "Inline") => {
    if (isAmpTemplate && mode !== "HTML") return;
    if (mode === editMode) return;
    if (isFullDocMode) {
      const monacoValue = monaco?.editor?.getEditors()[0]?.getValue();
      if (monacoValue !== undefined)
        setSplitContent(splitTemplate(monacoValue));
    } else if (editMode === "Inline") {
      const doc = inlineIframeRef.current?.contentDocument;
      if (doc?.documentElement)
        setSplitContent(splitTemplate(doc.documentElement.outerHTML));
    }
    const body = getCurrentBody();
    setCurrentBody(body);
    setEditMode(mode);
  };

  const getSaveReadyContent = (): string => {
    if (isAmpTemplate) {
      return monaco?.editor?.getEditors()[0]?.getValue() ?? currentBody;
    }
    if (isFullDocMode) {
      return (
        monaco?.editor?.getEditors()[0]?.getValue() ??
        joinTemplate(
          splitContent.templateHeader,
          currentBody,
          splitContent.templateFooter
        )
      );
    }
    if (editMode === "Inline") {
      const doc = inlineIframeRef.current?.contentDocument;
      return (
        doc?.documentElement.outerHTML ??
        joinTemplate(
          splitContent.templateHeader,
          currentBody,
          splitContent.templateFooter
        )
      );
    }
    const body = getCurrentBody();
    return joinTemplate(
      splitContent.templateHeader,
      body,
      splitContent.templateFooter
    );
  };

  const tearDownEditorAndClose = () => {
    const content = getSaveReadyContent();
    const editedContent = isAmpTemplate
      ? content
      : sanitizeEmailHtml(content, true);
    onClose({ editedData: editedContent, subject: emailSubject });
  };

  const validateAndSave = () => {
    const body = getCurrentBody();
    if (!body) return;
    setSaving(true);
    const content = getSaveReadyContent();
    const full = isAmpTemplate ? content : sanitizeEmailHtml(content, true);
    const base64Body = btoa(unescape(encodeURIComponent(full)));
    validateEmailTemplate(base64Body, templateType)
      .then(() => tearDownEditorAndClose())
      .catch((err) => {
        toast(err.response?.data?.errorRaw || "Template validation failed", {
          type: "error",
          autoClose: 10000,
        });
      })
      .finally(() => setSaving(false));
  };

  const insertInlineContent = (html: string) => {
    const win = inlineIframeRef.current?.contentWindow;
    const doc = inlineIframeRef.current?.contentDocument;
    const body = doc?.body;
    if (!win || !doc || !body) return;

    const sel = win.getSelection();
    const hasRangeInBody =
      sel &&
      sel.rangeCount > 0 &&
      body.contains(sel.getRangeAt(0).commonAncestorContainer);

    if (!hasRangeInBody) {
      const range = doc.createRange();
      range.selectNodeContents(body);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    body.focus();
    doc.execCommand("insertHTML", false, html);
  };

  const insertContentAtSelection = (html: string, editId: string) => {
    if (editMode === "Classic") {
      tinyEditorRef.current?.insertContent(html);
    } else if (editMode === "Inline") {
      insertInlineContent(html);
    } else {
      const monacoEditor = monaco?.editor?.getEditors()[0];
      if (monacoEditor) {
        const sel = monacoEditor.getSelection();
        monacoEditor.executeEdits(editId, [
          {
            range: sel ?? {
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: 1,
              endColumn: 1,
            },
            text: html,
          },
        ]);
      }
    }
  };

  const hasActiveSelection = (): boolean => {
    if (editMode === "Classic") {
      return Boolean(
        tinyEditorRef.current?.selection?.getContent({ format: "text" })?.trim()
      );
    }
    if (editMode === "Inline") {
      const sel = inlineIframeRef.current?.contentWindow?.getSelection();
      return Boolean(sel?.toString().trim());
    }
    const sel = monaco?.editor?.getEditors()[0]?.getSelection();
    return Boolean(sel && !sel.isEmpty());
  };

  const insertAsset = (url: string, name: string) => {
    const imgHtml = `<img src="${url}" alt="${name}" style="max-width:100%;height:auto;" />`;
    insertContentAtSelection(imgHtml, "insert-asset");
  };

  const handleAssetDelete = (assetId: string) => {
    setDeletingAssetId(assetId);
    deleteAsset(assetId, {
      onSuccess: () => {
        toast("Asset deleted", { type: "success", autoClose: 2000 });
      },
      onError: () => {
        toast("Failed to delete asset. Please try again.", { type: "error" });
      },
      onSettled: () => {
        setDeletingAssetId(null);
      },
    });
  };

  const handleAssetUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    uploadAsset(file, {
      onSuccess: () => {
        toast("Asset uploaded successfully", {
          type: "success",
          autoClose: 3000,
        });
      },
      onError: () => {
        toast("Failed to upload asset. Please try again.", { type: "error" });
      },
    });
  };

  const processWordFile = async (
    file: File,
    target: "body" | "selection" = "body"
  ) => {
    setImportingWord(true);
    try {
      const base64Docx = (await convertToBase64(file)) as string;
      const base64Html = await convertDocxToHtml(base64Docx);
      const sanitized = sanitizeEmailHtml(decodeBase64Utf8(base64Html), false);
      if (target === "selection") {
        insertContentAtSelection(sanitized, "import-word-selection");
      } else {
        setCurrentBody(sanitized);
        if (editMode === "Classic") {
          tinyEditorRef.current?.setContent(sanitized);
        }
      }
      toast("Word document imported successfully", {
        type: "success",
        autoClose: 3000,
      });
    } catch {
      toast("Failed to import the Word document. Please try again.", {
        type: "error",
      });
    } finally {
      setImportingWord(false);
    }
  };

  const handleWordImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast("Only .docx files are supported", { type: "error" });
      return;
    }
    if (getCurrentBody().trim()) {
      setPendingImportHasSelection(hasActiveSelection());
      setPendingWordFile(file);
    } else {
      processWordFile(file, "body");
    }
  };

  const confirmWordImport = (target: "body" | "selection") => {
    if (pendingWordFile) processWordFile(pendingWordFile, target);
    setPendingWordFile(null);
  };

  function handleMonacoEditorDidMount(editor: any, monacoInstance: any) {
    if (!monacoInstance) return;
    monacoInstance.languages.registerCompletionItemProvider("html", {
      triggerCharacters: ["?"],
      provideCompletionItems: (model: any, position: any) => {
        const wordBefore = model.getWordUntilPosition({
          lineNumber: position.lineNumber,
          column: position.column - 1,
        });
        const wordUntil = model.getWordUntilPosition(position);
        if (wordBefore.word.trim() === "" || wordUntil.word.trim() === "#") {
          const templateKeywords = templateVariablesRef.current.map((v) => ({
            label: v.variable,
            insertText: v.variable,
            description: `Example: ${v.exampleValue}`,
            kind: monacoInstance.languages.CompletionItemKind.Text,
            insertTextRules:
              monacoInstance.languages.CompletionItemInsertTextRule
                .InsertAsSnippet,
          }));
          const systemKeywords = systemVariablesRef.current.map((v) => ({
            label: v.variable,
            insertText: v.variable,
            description: `General field: ${v.label} (${v.description})`,
            kind: monacoInstance.languages.CompletionItemKind.Text,
            insertTextRules:
              monacoInstance.languages.CompletionItemInsertTextRule
                .InsertAsSnippet,
          }));
          const keywords = [...templateKeywords, ...systemKeywords];
          const suggestions = keywords.map((kw) => ({
            label: kw.label,
            kind: kw.kind,
            documentation: kw.description,
            insertText: kw.insertText,
            detail: kw.description,
            insertTextRules: kw.insertTextRules,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: wordUntil.startColumn,
              endLineNumber: position.lineNumber,
              endColumn: wordUntil.endColumn - 1,
            },
          }));
          return { suggestions };
        }
        return { suggestions: [] };
      },
    });
  }

  assetsRef.current = assets;
  templateVariablesRef.current = templateVariables;
  systemVariablesRef.current = systemVariables;

  const tinyMceInit = useMemo(
    () => ({
      height: 500,
      menubar: false,
      plugins: "link image lists table code",
      toolbar:
        "undo redo | formatselect | fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | link | removeformat",
      link_default_protocol: "https",
      link_target_list: [
        { title: "Open in the same tab", value: "" },
        { title: "Open in a new tab", value: "_blank" },
      ],
      automatic_uploads: false,
      image_advtab: true,
      content_style:
        "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } a { color:#E85936; font-weight:500; text-decoration:underline; text-underline-offset:2px; } a:hover { color:#D15031; }",
      images_upload_handler: (blobInfo: any) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blobInfo.blob());
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () =>
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ message: "Image upload failed", remove: true });
        }),
      paste_data_images: true,
      paste_word_valid_elements:
        "b,strong,i,em,u,p,br,ul,ol,li,h1,h2,h3,h4,a[href],table,tr,td,th,span",
      paste_retain_style_properties:
        "color font-weight font-style text-decoration text-align",
      paste_preprocess: (_plugin: any, args: { content: string }) => {
        args.content = args.content
          .replace(/mso-[^:;"]+:[^;"]+;?/gi, "")
          .replace(/<span[^>]*style="\s*"[^>]*>(.*?)<\/span>/gi, "$1");
      },
      paste_postprocess: (_plugin: any, args: { node: HTMLElement }) => {
        args.node.innerHTML = sanitizeEmailHtml(args.node.innerHTML, false);
      },
      setup: (editor: any) => {
        editor.ui.registry.addMenuButton("insertVariable", {
          text: "Variables",
          tooltip: "Insert template variable",
          fetch: (callback: (items: any[]) => void) => {
            const templateItems = templateVariablesRef.current.map((v) => ({
              type: "menuitem" as const,
              text: `${v.variable} (${v.exampleValue})`,
              onAction: () => editor.insertContent(v.variable),
            }));
            const systemItems = systemVariablesRef.current.map((v) => ({
              type: "menuitem" as const,
              text: `${v.variable} (General: ${v.label})`,
              onAction: () => editor.insertContent(v.variable),
            }));
            const list = [...templateItems, ...systemItems];
            const items =
              list.length > 0
                ? list
                : [
                    {
                      type: "menuitem" as const,
                      text: "No variables available",
                      onAction: () => {},
                    },
                  ];
            callback(items);
          },
        });

        editor.ui.registry.addMenuButton("insertAsset", {
          text: "Assets",
          icon: "image",
          tooltip: "Insert an uploaded asset",
          fetch: (callback: (items: any[]) => void) => {
            const list = assetsRef.current;
            const items =
              list.length > 0
                ? list.map((asset) => ({
                    type: "menuitem" as const,
                    text: asset.name,
                    onAction: () =>
                      editor.insertContent(
                        `<img src="${asset.s3Path}" alt="${asset.name}" style="max-width:100%;height:auto;" />`
                      ),
                  }))
                : [
                    {
                      type: "menuitem" as const,
                      text: "No assets uploaded yet",
                      onAction: () => {},
                    },
                  ];
            callback(items);
          },
        });
      },
    }),
    []
  );

  const canEdit = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.CompanySettings,
    DefaultAction.All
  );

  const variableSearchLower = variableSearch.trim().toLowerCase();
  const filteredTemplateVariables = variableSearchLower
    ? templateVariables.filter((v) =>
        `${v.variable} ${v.exampleValue}`
          .toLowerCase()
          .includes(variableSearchLower)
      )
    : templateVariables;
  const filteredSystemVariables = variableSearchLower
    ? systemVariables.filter((v) =>
        `${v.variable} ${v.label} ${v.description}`
          .toLowerCase()
          .includes(variableSearchLower)
      )
    : systemVariables;

  return (
    <div className="flex flex-col">
      <div className="p-4 px-10 mt-4 ml-3 text-lg font-medium border-b">
        <h6 className="flex justify-between items-center">
          Edit {templateName}
          <HStack className="items-center gap-3">
            <IconButton onClick={onToggleFullscreen} size="small">
              <Icon
                icon={isFullscreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"}
                height={20}
              />
            </IconButton>
            <span
              onClick={() => onClose({})}
              className="cursor-pointer text-base"
            >
              ✕
            </span>
          </HStack>
        </h6>
      </div>

      <VStack className="gap-4 p-4 px-10">
        {(description || recipient) && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Icon
              icon="mdi:email-outline"
              className="shrink-0 text-primary"
              height={16}
            />
            {description && <span>{description}</span>}
            {description && recipient && <span aria-hidden="true">•</span>}
            {recipient && (
              <span>
                Sent to:{" "}
                <span className="font-medium text-gray-700">{recipient}</span>
              </span>
            )}
          </div>
        )}
        <VStack className="gap-2 border-b border-gray-100 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Editor mode
              </span>
              <div
                role="radiogroup"
                aria-label="Email editor mode"
                className="flex items-center gap-4"
              >
                {!isAmpTemplate && (
                  <label
                    title="Use for normal copy, formatting, links, images, variables, and text alignment."
                    className={`flex cursor-pointer items-center gap-1.5 text-sm transition-colors ${
                      editMode === "Classic"
                        ? "font-semibold text-primary"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    <input
                      type="radio"
                      name="email-editor-mode"
                      className="cursor-pointer accent-orange-500"
                      checked={editMode === "Classic"}
                      onChange={() => handleModeChange("Classic")}
                    />
                    Standard
                  </label>
                )}
                <label
                  title="Use only when you need to edit markup or styles and are comfortable working with HTML."
                  className={`flex cursor-pointer items-center gap-1.5 text-sm transition-colors ${
                    editMode === "HTML"
                      ? "font-semibold text-primary"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="email-editor-mode"
                    className="cursor-pointer accent-orange-500"
                    checked={editMode === "HTML"}
                    onChange={() => handleModeChange("HTML")}
                  />
                  Advanced HTML
                </label>
                {isInternalUser && !isAmpTemplate && (
                  <label
                    title="Internal use only. Edit the rendered email directly when visual layout changes need full-template context."
                    className={`flex cursor-pointer items-center gap-1.5 text-sm transition-colors ${
                      editMode === "Inline"
                        ? "font-semibold text-primary"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    <input
                      type="radio"
                      name="email-editor-mode"
                      className="cursor-pointer accent-orange-500"
                      checked={editMode === "Inline"}
                      onChange={() => handleModeChange("Inline")}
                    />
                    Inline
                  </label>
                )}
              </div>
            </div>

            {!isUserEsopViewer && canEdit && (
              <HStack className="gap-2 items-center">
                <ButtonSecondary
                  className="!px-2.5 !py-1.5"
                  onClick={() => setShowAssets((prev) => !prev)}
                >
                  {showAssets ? "Hide Assets" : "Assets"}
                </ButtonSecondary>
                <ButtonSecondary
                  className="!px-2.5 !py-1.5"
                  onClick={() => setShowVariables((prev) => !prev)}
                >
                  {showVariables ? "Hide Variables" : "Variables"}
                </ButtonSecondary>
                {!isAmpTemplate && (
                  <ButtonPrimary1
                    className="!px-2.5 !py-1.5"
                    onClick={() => wordFileInputRef.current?.click()}
                    loading={importingWord}
                  >
                    Import Word
                  </ButtonPrimary1>
                )}
              </HStack>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {editMode === "Classic" &&
              "Standard is recommended for copy, formatting, links, images, and variables."}
            {editMode === "HTML" &&
              (isAmpTemplate
                ? "AMP templates are edited as complete documents. Required scripts, forms, and action tokens are validated before saving."
                : "Use Advanced HTML only when you need to edit markup or styles.")}
            {editMode === "Inline" &&
              "Internal use only: edit the rendered email with full-template context."}
          </p>
          <input
            ref={wordFileInputRef}
            type="file"
            accept=".docx"
            className="hidden"
            onChange={handleWordImport}
          />
        </VStack>

        <HStack className="items-center gap-2">
          <Label className="whitespace-nowrap mt-0">Subject:</Label>
          <Input
            className="flex-1 px-2 py-2"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Email subject"
          />
        </HStack>

        <HStack className="items-start gap-4 w-full">
          {showAssets && (
            <div
              className="flex flex-col w-[200px] shrink-0 border rounded-lg bg-gray-50 overflow-hidden"
              style={{ height: `${PANE_HEIGHT}px` }}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b bg-white shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Assets
                </span>
                <HStack className="items-center gap-1">
                  <ButtonPrimary1
                    disabled={assetUploading || isUserEsopViewer || !canEdit}
                    onClick={() => assetInputRef.current?.click()}
                    loading={assetUploading}
                  >
                    Upload
                  </ButtonPrimary1>
                  <IconButton
                    size="small"
                    title="Close"
                    onClick={() => setShowAssets(false)}
                  >
                    <Icon icon="mdi:close" height={16} />
                  </IconButton>
                </HStack>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {assetsLoading ? (
                  <p className="text-xs text-gray-400 mt-2">Loading…</p>
                ) : assets.length === 0 ? (
                  <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                    No assets yet. Upload images to insert them into templates.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {assets.map((asset) => (
                      <div
                        key={asset.id}
                        className="group relative border rounded bg-white overflow-hidden cursor-pointer hover:border-orange-400 transition-colors"
                        title={`Click to insert: ${asset.name}`}
                        onClick={() => insertAsset(asset.s3Path, asset.name)}
                      >
                        <img
                          src={asset.s3Path}
                          alt={asset.name}
                          className="object-contain w-full h-16 p-1"
                        />
                        <div className="px-1 pb-1 truncate text-xs text-gray-500 text-center">
                          {asset.name}
                        </div>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-none">
                          <span className="text-white text-xs font-medium">
                            Insert
                          </span>
                        </div>
                        {canEdit && !isUserEsopViewer && (
                          <button
                            type="button"
                            disabled={deletingAssetId === asset.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssetDelete(asset.id);
                            }}
                            title="Delete asset"
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white disabled:opacity-60 transition-opacity"
                          >
                            {deletingAssetId === asset.id ? (
                              <span className="text-[10px] leading-none">
                                …
                              </span>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <input
                ref={assetInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.svg,.webp"
                className="hidden"
                onChange={handleAssetUpload}
              />
            </div>
          )}

          {showVariables && (
            <div
              className="flex flex-col w-[220px] shrink-0 border rounded-lg bg-gray-50 overflow-hidden"
              style={{ height: `${PANE_HEIGHT}px` }}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b bg-white shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Variables
                </span>
                <IconButton
                  size="small"
                  title="Close"
                  onClick={() => setShowVariables(false)}
                >
                  <Icon icon="mdi:close" height={16} />
                </IconButton>
              </div>
              <div className="px-2 pt-2 shrink-0">
                <input
                  className="flex-1 px-2 py-2 form-input bg-white"
                  placeholder="Search variables…"
                  value={variableSearch}
                  onChange={(e) => setVariableSearch(e.target.value)}
                />
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {variablesLoading ? (
                  <p className="text-xs text-gray-400 mt-2">Loading…</p>
                ) : filteredTemplateVariables.length === 0 ? (
                  <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                    {variableSearchLower
                      ? "No matching variables."
                      : "No variables available for this template type."}
                  </p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {filteredTemplateVariables.map((v) => (
                      <div
                        key={v.variable}
                        className="border rounded bg-white p-2 cursor-pointer hover:border-orange-400 transition-colors"
                        title={`Click to insert ${v.variable}`}
                        onClick={() =>
                          insertContentAtSelection(
                            v.variable,
                            "insert-variable"
                          )
                        }
                      >
                        <div className="text-xs font-mono text-gray-700 truncate">
                          {v.variable}
                        </div>
                        <div className="text-[10px] text-gray-400 truncate">
                          {v.exampleValue}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 pt-2 border-t">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    General Fields
                  </span>
                  <p className="text-[10px] text-gray-400 mt-0.5 mb-1.5 leading-relaxed">
                    Employee/Company fields from Custom Email Campaign. These
                    may not apply to every template type.
                  </p>
                  {systemVariablesLoading ? (
                    <p className="text-xs text-gray-400">Loading…</p>
                  ) : filteredSystemVariables.length === 0 ? (
                    <p className="text-xs text-gray-400 leading-relaxed">
                      {variableSearchLower
                        ? "No matching fields."
                        : "No general fields available."}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {filteredSystemVariables.map((v) => (
                        <div
                          key={v.variable}
                          className="border rounded bg-white p-2 cursor-pointer hover:border-orange-400 transition-colors"
                          title={`Click to insert ${v.variable}`}
                          onClick={() =>
                            insertContentAtSelection(
                              v.variable,
                              "insert-system-variable"
                            )
                          }
                        >
                          <div className="text-xs font-mono text-gray-700 truncate">
                            {v.variable}
                          </div>
                          <div className="text-[10px] text-gray-400 truncate">
                            {v.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 min-w-0">
            {editMode === "Classic" && (
              <TinyEditor
                apiKey={TINYMCE_API_KEY}
                onInit={(_evt, editor) => {
                  tinyEditorRef.current = editor;
                  applyTinyMceHeight(editor);
                }}
                initialValue={splitContent.templateBody}
                onEditorChange={(content) => setCurrentBody(content)}
                init={tinyMceInit}
              />
            )}
            {editMode === "HTML" && (
              <Editor
                onMount={handleMonacoEditorDidMount}
                options={monacoEditorOptions}
                language="html"
                height={`${PANE_HEIGHT}px`}
                defaultLanguage="html"
                theme=""
                value={
                  isAmpTemplate
                    ? currentBody
                    : isFullDocMode
                    ? joinTemplate(
                        splitContent.templateHeader,
                        currentBody,
                        splitContent.templateFooter
                      )
                    : currentBody
                }
                onChange={(value) => {
                  const v = value ?? "";
                  if (isAmpTemplate) {
                    setCurrentBody(v);
                  } else if (isFullDocMode) {
                    const split = splitTemplate(v);
                    setSplitContent(split);
                    setCurrentBody(split.templateBody);
                  } else {
                    setCurrentBody(v);
                  }
                }}
              />
            )}
            {editMode === "Inline" && (
              <iframe
                ref={inlineIframeRef}
                onLoad={handleInlineLoad}
                srcDoc={inlineSrcDoc}
                title="Inline Editor"
                className="border-0 bg-white w-full"
                style={{ height: PANE_HEIGHT, display: "block" }}
                sandbox="allow-same-origin"
              />
            )}
          </div>

          {editMode !== "Inline" && (
            <div
              className="flex flex-col flex-1 min-w-0 border rounded-lg overflow-hidden"
              style={{ height: `${PANE_HEIGHT}px` }}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Live Preview
                </span>
                <HStack className="gap-1">
                  <Badge
                    badgeContent={outlookIssues.length}
                    color="error"
                    invisible={outlookIssues.length === 0}
                    overlap="circular"
                  >
                    <IconButton
                      size="small"
                      title="Check Outlook compatibility"
                      onClick={() => setShowOutlookCheck((prev) => !prev)}
                      className={
                        outlookIssues.length > 0
                          ? "text-red-500"
                          : showOutlookCheck
                          ? "text-orange-500"
                          : ""
                      }
                    >
                      <Icon icon="mdi:microsoft-outlook" height={16} />
                    </IconButton>
                  </Badge>
                  <Badge
                    badgeContent={tagBalanceIssues.length}
                    color="error"
                    invisible={tagBalanceIssues.length === 0}
                    overlap="circular"
                  >
                    <IconButton
                      size="small"
                      title="Check for unclosed/mismatched tags"
                      onClick={() => setShowTagBalanceCheck((prev) => !prev)}
                      className={
                        tagBalanceIssues.length > 0
                          ? "text-red-500"
                          : showTagBalanceCheck
                          ? "text-orange-500"
                          : ""
                      }
                    >
                      <Icon icon="mdi:code-tags-check" height={16} />
                    </IconButton>
                  </Badge>
                </HStack>
              </div>
              {showOutlookCheck && (
                <div className="border-b bg-amber-50 max-h-32 overflow-y-auto p-2 text-xs shrink-0">
                  {outlookIssues.length === 0 ? (
                    <p className="text-green-700">
                      ✓ No known Outlook compatibility issues detected. (This is
                      a heuristic scan, not a real Outlook render: Outlook
                      desktop uses Word&rsquo;s rendering engine, not a
                      browser.)
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {outlookIssues.map((issue) => (
                        <li
                          key={issue.id}
                          className={
                            issue.severity === "error"
                              ? "text-red-700"
                              : "text-amber-700"
                          }
                        >
                          {issue.severity === "error" ? "⚠️ " : "ℹ️ "}
                          {issue.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {showTagBalanceCheck && (
                <div className="border-b bg-red-50 max-h-32 overflow-y-auto p-2 text-xs shrink-0">
                  {tagBalanceIssues.length === 0 ? (
                    <p className="text-green-700">
                      ✓ No unclosed or mismatched tags found.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-red-700 font-medium">
                          {tagBalanceIssues.length} tag issue
                          {tagBalanceIssues.length > 1 ? "s" : ""} found
                        </span>
                        <ButtonSecondary
                          className="!py-0.5 !px-2 text-xs"
                          onClick={() => handleModeChange("HTML")}
                        >
                          Go to Advanced to fix
                        </ButtonSecondary>
                      </div>
                      <ul className="space-y-1">
                        {tagBalanceIssues.map((issue, idx) => (
                          <li key={idx} className="text-red-700">
                            ⚠️ {issue.message}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              <div className="flex-1 overflow-auto bg-gray-100">
                {isAmpTemplate && ampPreviewLoading && (
                  <div className="px-3 py-2 text-xs text-blue-800 bg-blue-50">
                    Rendering AMP preview…
                  </div>
                )}
                {isAmpTemplate && ampPreviewError && (
                  <div className="px-3 py-2 text-xs text-red-700 bg-red-50">
                    {ampPreviewError}
                  </div>
                )}
                <iframe
                  ref={previewIframeRef}
                  onLoad={handlePreviewLoad}
                  srcDoc={
                    renderedPreviewHtml ||
                    "<div style='padding:16px;color:#aaa;font-family:sans-serif;'>Start typing to see a preview…</div>"
                  }
                  title="Email Preview"
                  className="border-0 bg-white"
                  style={{
                    height: PANE_HEIGHT,
                    width: "100%",
                    display: "block",
                  }}
                  sandbox={
                    isAmpTemplate
                      ? "allow-scripts allow-forms allow-same-origin"
                      : "allow-same-origin"
                  }
                />
              </div>
            </div>
          )}
        </HStack>

        {!isUserEsopViewer && (
          <HStack className="flex-row-reverse gap-4">
            <ButtonPrimary
              className={`${
                !canEdit ? "opacity-50 cursor-not-allowed" : ""
              } flex items-center justify-center`}
              onClick={validateAndSave}
              disabled={saving || !canEdit}
            >
              {saving ? "Validating…" : "Save"}
            </ButtonPrimary>
            <ButtonSecondary
              onClick={() => onClose({})}
              className="text-gray-400 bg-slate-50"
            >
              Cancel
            </ButtonSecondary>
          </HStack>
        )}
      </VStack>

      <Dialog
        open={!!pendingWordFile}
        onClose={() => setPendingWordFile(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between border-b">
          <span className="flex items-center gap-2 text-base font-semibold text-gray-800">
            <Icon
              icon="mdi:file-word-outline"
              className="text-orange-500"
              height={22}
            />
            Import from Word
          </span>
          <IconButton onClick={() => setPendingWordFile(null)} size="small">
            <Icon icon="mdi:close" height={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-5">
          <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-100 rounded-lg">
            <Icon
              icon="mdi:alert-outline"
              className="text-orange-500 shrink-0 mt-0.5"
              height={16}
            />
            <p className="text-sm text-orange-700 italic">
              {pendingImportHasSelection
                ? "You have text selected. Choose whether to replace just the selection or the entire body. Some formatting from the Word document (custom colors, fonts, or highlights) may not be preserved exactly."
                : "Importing will replace the current body content. Some formatting from the Word document (custom colors, fonts, or highlights) may not be preserved exactly."}
            </p>
          </div>
        </DialogContent>
        <DialogActions className="border-t px-5 py-3 bg-gray-50">
          <ButtonSecondary onClick={() => setPendingWordFile(null)}>
            Cancel
          </ButtonSecondary>
          {pendingImportHasSelection && (
            <ButtonSecondary onClick={() => confirmWordImport("selection")}>
              Replace Selection
            </ButtonSecondary>
          )}
          <ButtonPrimary onClick={() => confirmWordImport("body")}>
            Replace Entire Body
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EmailTemplateEditorEnhanced;
